import type { Row, DashboardData } from "./types";
import type { OrigemQuestoes, QuestaoDiscGabarito } from "./disc-modelo";
import type { TipoRetorno } from "./retorno-entrevista";

const BASE = "/api";

/**
 * Usuário logado nesta sessão. É enviado no header das operações de usuários
 * para o servidor validar que apenas administradores criam/editam/excluem.
 */
let sessionUser = "";
export function setSessionUser(u: string) {
  sessionUser = u ?? "";
}
const authHeaders = () => ({ "x-sercamp-usuario": sessionUser });

export class ApiError extends Error {
  code: string;
  constructor(message: string, code = "ERROR") {
    super(message);
    this.code = code;
  }
}

async function handle(res: Response) {
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    let code = "ERROR";
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      if (body?.code) code = body.code;
    } catch {
      /* ignora */
    }
    throw new ApiError(msg, code);
  }
  return res.json();
}

export const api = {
  /**
   * PLANILHA de colaboradores: exportar em Excel, baixar o modelo e importar
   * cruzando com quem já existe. Ver server/colaboradores-planilha.ts.
   */
  planilha: {
    /** Baixa o .xlsx (exportação ou modelo) pelo próprio navegador. */
    baixar: async (qual: "exportar" | "modelo"): Promise<void> => {
      const res = await fetch(`${BASE}/colaboradores/planilha/${qual}`, { headers: authHeaders() });
      if (!res.ok) {
        let msg = `Erro ${res.status}`;
        try {
          msg = (await res.json())?.error ?? msg;
        } catch {
          /* resposta sem json */
        }
        throw new ApiError(msg);
      }
      const nome =
        /filename\*=UTF-8''([^;]+)/.exec(res.headers.get("content-disposition") ?? "")?.[1];
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = nome ? decodeURIComponent(nome) : "colaboradores.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },

    /** Sem `confirmar`, é só a prévia: nada é gravado. */
    importar: (arquivo: File, confirmar: boolean, aba?: string): Promise<any> => {
      const fd = new FormData();
      fd.append("file", arquivo);
      fd.append("confirmar", confirmar ? "1" : "0");
      if (aba) fd.append("aba", aba);
      return fetch(`${BASE}/colaboradores/planilha/importar`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },
  },

  /**
   * AVALIAÇÃO DE DESEMPENHO: a lista por colaborador e o plano de ação dele.
   * Ver server/planos-acao.ts.
   */
  desempenho: {
    listar: (): Promise<{ colaboradores: Row[]; resumo: Row }> =>
      fetch(`${BASE}/desempenho`, { headers: authHeaders() }).then(handle),

    /** Sugere as ações do plano (IA, com roteiro padrão de reserva). */
    sugerirAcoes: (colaboradorId: number, contexto?: string): Promise<any> =>
      fetch(`${BASE}/desempenho/${colaboradorId}/sugerir-acoes`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ contexto: contexto ?? "" }),
      }).then(handle),

    plano: (id: number): Promise<{ plano: Row; itens: Row[]; avaliacoes: Row[] }> =>
      fetch(`${BASE}/planos-acao/${id}`, { headers: authHeaders() }).then(handle),

    criarPlano: (dados: Record<string, unknown>): Promise<{ ok: boolean; id: number }> =>
      fetch(`${BASE}/planos-acao`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    atualizarPlano: (id: number, dados: Record<string, unknown>): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/planos-acao/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    removerPlano: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/planos-acao/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),

    criarItem: (planoId: number, dados: Record<string, unknown>): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/planos-acao/${planoId}/itens`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    atualizarItem: (itemId: number, dados: Record<string, unknown>): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/planos-acao/itens/${itemId}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    removerItem: (itemId: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/planos-acao/itens/${itemId}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * ENDOMARKETING: comunicado no WhatsApp, foto do colaborador, eventos do ano
   * (pastas de imagens) e aniversariantes. Ver server/endomarketing.ts.
   */
  endomarketing: {
    /** Resumo da primeira tela: aniversários de hoje/semana, pendências, último evento. */
    painel: (): Promise<any> =>
      fetch(`${BASE}/endomarketing/painel`, { headers: authHeaders() }).then(handle),

    aniversariantes: (mes: number): Promise<any> =>
      fetch(`${BASE}/endomarketing/aniversariantes?mes=${mes}`, { headers: authHeaders() }).then(handle),

    /** Marca (ou desmarca) que a pessoa já foi parabenizada neste ano. */
    parabens: (
      colaborador_id: number,
      tipo: "nascimento" | "empresa",
      desfazer = false,
    ): Promise<{ ok: boolean; parabenizado: boolean }> =>
      fetch(`${BASE}/endomarketing/parabens`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ colaborador_id, tipo, desfazer }),
      }).then(handle),

    fotos: (): Promise<{ colaboradores: any[]; com_foto: number; total: number }> =>
      fetch(`${BASE}/endomarketing/fotos`, { headers: authHeaders() }).then(handle),

    salvarFoto: (id: number, foto: string): Promise<{ ok: boolean; mensagem: string }> =>
      fetch(`${BASE}/endomarketing/fotos/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ foto }),
      }).then(handle),

    salvarNascimento: (id: number, data_nascimento: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/endomarketing/nascimento/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ data_nascimento }),
      }).then(handle),

    comunicados: (): Promise<any[]> =>
      fetch(`${BASE}/endomarketing/comunicados`, { headers: authHeaders() }).then(handle),

    registrarComunicado: (data: Record<string, unknown>): Promise<{ ok: boolean; id: number }> =>
      fetch(`${BASE}/endomarketing/comunicados`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    removerComunicado: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/endomarketing/comunicados/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /**
     * ARTE / MÍDIA do comunicado (imagem, vídeo ou PDF que vai junto no
     * WhatsApp). Sobe como arquivo — a arte gerada pelo sistema vira File antes
     * de subir — e o que fica guardado no comunicado é só o NOME devolvido aqui.
     */
    arte: {
      enviar: (
        arquivo: File,
      ): Promise<{ ok: boolean; arquivo: string; tipo: TipoMidia; tamanho: number }> => {
        const form = new FormData();
        form.append("file", arquivo);
        return fetch(`${BASE}/endomarketing/artes`, {
          method: "POST",
          headers: authHeaders(),
          body: form,
        }).then(handle);
      },

      /** Endereço do arquivo (serve direto na tag <img>/<video> e no link do PDF). */
      url: (arquivo: string): string =>
        `${BASE}/endomarketing/artes/${encodeURIComponent(arquivo)}`,

      remover: (arquivo: string): Promise<{ ok: boolean }> =>
        fetch(`${BASE}/endomarketing/artes/${encodeURIComponent(arquivo)}`, {
          method: "DELETE",
          headers: authHeaders(),
        }).then(handle),
    },

    /**
     * FUNDOS do cartão de aniversário: a imagem de fundo que fica salva no
     * servidor para todo o RH reaproveitar (ex.: o modelo do mês feito no
     * Canva). Excluir é só de administrador.
     */
    fundos: {
      listar: (): Promise<FundoCartao[]> =>
        fetch(`${BASE}/endomarketing/fundos`, { headers: authHeaders() }).then(handle),

      enviar: (arquivo: File): Promise<{ ok: boolean; id: number; arquivo: string; url: string }> => {
        const form = new FormData();
        form.append("file", arquivo);
        return fetch(`${BASE}/endomarketing/fundos`, {
          method: "POST",
          headers: authHeaders(),
          body: form,
        }).then(handle);
      },

      /** Endereço da imagem (miniatura e canvas do cartão — mesma origem, não "suja" o canvas). */
      url: (arquivo: string): string =>
        `${BASE}/endomarketing/fundos/${encodeURIComponent(arquivo)}`,

      remover: (arquivo: string): Promise<{ ok: boolean }> =>
        fetch(`${BASE}/endomarketing/fundos/${encodeURIComponent(arquivo)}`, {
          method: "DELETE",
          headers: authHeaders(),
        }).then(handle),
    },

    eventos: (): Promise<any[]> =>
      fetch(`${BASE}/endomarketing/eventos`, { headers: authHeaders() }).then(handle),

    /**
     * Lembretes que vencem hoje (aviso na tela). O envio automático virá
     * depois — a estrutura já está pronta para ele.
     */
    lembretesDeHoje: (): Promise<any[]> =>
      fetch(`${BASE}/endomarketing/eventos/lembretes-de-hoje`, { headers: authHeaders() }).then(handle),

    dispensarLembrete: (lembreteId: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/endomarketing/eventos/lembretes/${lembreteId}/visto`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    /**
     * CENTRAL DO EVENTO — o evento inteiro numa chamada (ficha, programação,
     * equipe, participantes, checklist, orçamento, links, tags, lembretes,
     * histórico e os números já somados). Ver server/eventos.ts.
     */
    evento: {
      central: (id: number): Promise<any> =>
        fetch(`${BASE}/endomarketing/eventos/${id}/central`, { headers: authHeaders() }).then(handle),

      /**
       * Grava a ficha. Manda SÓ os campos da aba que está sendo salva — o
       * servidor preserva o resto (por isso salvar "Comunicação" não apaga
       * "Data e local").
       */
      detalhes: (id: number, dados: Record<string, unknown>): Promise<{ ok: boolean; mensagem: string }> =>
        fetch(`${BASE}/endomarketing/eventos/${id}/detalhes`, {
          method: "PUT",
          headers: { "content-type": "application/json", ...authHeaders() },
          body: JSON.stringify(dados),
        }).then(handle),

      /**
       * Grava uma LISTA inteira do evento de uma vez (a tela edita tudo e
       * salva junto, como o plano de ação do RH).
       * lista: atividades | equipe | participantes | checklist | despesas | links | lembretes
       */
      lista: (
        id: number,
        lista: string,
        itens: Record<string, unknown>[],
      ): Promise<{ ok: boolean; total: number; mensagem: string }> =>
        fetch(`${BASE}/endomarketing/eventos/${id}/${lista}`, {
          method: "PUT",
          headers: { "content-type": "application/json", ...authHeaders() },
          body: JSON.stringify({ itens }),
        }).then(handle),

      /**
       * Gera os eventos da repetição (semanal, mensal, anual…). Não duplica: as
       * datas que a série já tem são puladas.
       */
      gerarOcorrencias: (
        id: number,
      ): Promise<{ ok: boolean; criadas: number; datas: string[]; mensagem: string }> =>
        fetch(`${BASE}/endomarketing/eventos/${id}/gerar-ocorrencias`, {
          method: "POST",
          headers: authHeaders(),
        }).then(handle),

      /** ANEXOS do evento (documentos — separados do álbum de fotos). */
      anexos: {
        listar: (id: number): Promise<any[]> =>
          fetch(`${BASE}/endomarketing/eventos/${id}/anexos`, { headers: authHeaders() }).then(handle),

        enviar: (id: number, files: FileList | File[]): Promise<{ ok: boolean; salvos: number }> => {
          const fd = new FormData();
          for (const f of Array.from(files)) fd.append("file", f);
          return fetch(`${BASE}/endomarketing/eventos/${id}/anexos`, {
            method: "POST",
            headers: authHeaders(),
            body: fd,
          }).then(handle);
        },

        remover: (id: number, arquivo: string): Promise<{ ok: boolean; mensagem: string }> =>
          fetch(`${BASE}/endomarketing/eventos/${id}/anexos/${encodeURIComponent(arquivo)}`, {
            method: "DELETE",
            headers: authHeaders(),
          }).then(handle),
      },

      /** Acrescenta vários colaboradores como participantes de uma vez. */
      participantesEmLote: (
        id: number,
        colaboradores: number[],
      ): Promise<{ ok: boolean; novos: number; mensagem: string }> =>
        fetch(`${BASE}/endomarketing/eventos/${id}/participantes/lote`, {
          method: "POST",
          headers: { "content-type": "application/json", ...authHeaders() },
          body: JSON.stringify({ colaboradores }),
        }).then(handle),
    },

    atualizarEvento: (id: number, data: Record<string, unknown>): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/endomarketing/eventos/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    /** Legenda da foto e/ou "usar como capa" do evento. */
    atualizarFotoEvento: (
      id: number,
      arquivo: string,
      data: { legenda?: string; capa?: boolean },
    ): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/endomarketing/eventos/${id}/fotos/${encodeURIComponent(arquivo)}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    criarEvento: (data: Record<string, unknown>): Promise<{ ok: boolean; id: number }> =>
      fetch(`${BASE}/endomarketing/eventos`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    removerEvento: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/endomarketing/eventos/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),

    fotosEvento: (id: number): Promise<any[]> =>
      fetch(`${BASE}/endomarketing/eventos/${id}/fotos`, { headers: authHeaders() }).then(handle),

    enviarFotosEvento: (id: number, files: FileList | File[]): Promise<{ ok: boolean; salvos: number }> => {
      const fd = new FormData();
      for (const f of Array.from(files)) fd.append("file", f);
      return fetch(`${BASE}/endomarketing/eventos/${id}/fotos`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    removerFotoEvento: (id: number, arquivo: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/endomarketing/eventos/${id}/fotos/${encodeURIComponent(arquivo)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * PROGRAMAS INTERNOS — "Realize um Sonho" e "Ideias que Transformam".
   *
   * São dois programas separados: `programa` é sempre "sonho" ou "ideia", e
   * cada um tem as suas campanhas (edições) e as suas respostas. O conteúdo dos
   * materiais (regras, categorias, prêmios) vive em src/programas.ts.
   * Ver server/programas.ts.
   */
  programas: {
    campanhas: (programa: string): Promise<CampanhaPrograma[]> =>
      fetch(`${BASE}/programas/${programa}/campanhas`, { headers: authHeaders() }).then(handle),

    criarCampanha: (
      programa: string,
      dados: Record<string, unknown>,
    ): Promise<{ id: number; token: string }> =>
      fetch(`${BASE}/programas/${programa}/campanhas`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    atualizarCampanha: (id: number, dados: Record<string, unknown>): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/programas/campanhas/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    removerCampanha: (id: number): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/programas/campanhas/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /** As respostas do programa (todas, ou só as de uma campanha). */
    respostas: (programa: string, campanhaId?: number | null): Promise<RespostaPrograma[]> =>
      fetch(
        `${BASE}/programas/${programa}/respostas${campanhaId ? `?campanha=${campanhaId}` : ""}`,
        { headers: authHeaders() },
      ).then(handle),

    avaliar: (
      programa: string,
      id: number,
      dados: Record<string, unknown>,
    ): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/programas/${programa}/respostas/${id}/avaliar`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    removerResposta: (programa: string, id: number): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/programas/${programa}/respostas/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /* --- página pública (o colaborador, sem login) --- */
    formulario: (
      token: string,
    ): Promise<{ programa: string; campanha: string; descricao: string | null }> =>
      fetch(`${BASE}/programa/formulario/${encodeURIComponent(token)}`).then(handle),

    responder: (
      token: string,
      dados: Record<string, unknown>,
    ): Promise<{ ok: boolean; id: number; mensagem: string }> =>
      fetch(`${BASE}/programa/formulario/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(dados),
      }).then(handle),
  },

  /**
   * TRIAGEM DE CURRÍCULO: vaga + perguntas estratégicas (IA) + link público
   * enviado ao candidato por WhatsApp. Ver server/triagem.ts.
   */
  triagens: {
    list: (): Promise<any[]> => fetch(`${BASE}/triagens`, { headers: authHeaders() }).then(handle),

    get: (id: number): Promise<any> =>
      fetch(`${BASE}/triagens/${id}`, { headers: authHeaders() }).then(handle),

    create: (data: Record<string, unknown>): Promise<{ id: number; token: string }> =>
      fetch(`${BASE}/triagens`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    update: (id: number, data: Record<string, unknown>): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/triagens/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    remove: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/triagens/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),

    /** A IA SUGERE as perguntas da função (nada é gravado até salvar). */
    perguntasIa: (
      id: number,
      vaga: Record<string, unknown>,
    ): Promise<{ perguntas: { id: string; texto: string }[] }> =>
      fetch(`${BASE}/triagens/${id}/perguntas-ia`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(vaga),
      }).then(handle),

    avaliar: (respostaId: number, data: Record<string, unknown>): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/triagens/respostas/${respostaId}/avaliar`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    /** O RH LANÇA um candidato à mão (currículo que chegou por fora do link). */
    criarCandidato: (
      triagemId: number,
      dados: Record<string, unknown>,
    ): Promise<{ ok: boolean; id: number; mensagem: string }> =>
      fetch(`${BASE}/triagens/${triagemId}/candidatos`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** Tira o candidato da vaga (o currículo dele vai junto). */
    removerCandidato: (triagemId: number, respostaId: number): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/triagens/${triagemId}/candidatos/${respostaId}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /**
     * CURRÍCULO do candidato. O candidato anexa sozinho pelo link público; aqui
     * é o lado do RH — anexar o que chegou por e-mail, abrir e apagar.
     */
    curriculo: {
      list: (triagemId: number, respostaId: number): Promise<ArquivoInfo[]> =>
        fetch(`${BASE}/triagens/${triagemId}/candidatos/${respostaId}/arquivos`, {
          headers: authHeaders(),
        }).then(handle),

      upload: (
        triagemId: number,
        respostaId: number,
        files: File[] | FileList,
      ): Promise<{ ok: boolean; salvos: string[] }> => {
        const fd = new FormData();
        for (const f of Array.from(files)) fd.append("file", f);
        return fetch(`${BASE}/triagens/${triagemId}/candidatos/${respostaId}/arquivos`, {
          method: "POST",
          headers: authHeaders(),
          body: fd,
        }).then(handle);
      },

      url: (triagemId: number, respostaId: number, nome: string): string =>
        `${BASE}/triagens/${triagemId}/candidatos/${respostaId}/arquivos/${encodeURIComponent(nome)}`,

      remove: (triagemId: number, respostaId: number, nome: string): Promise<{ ok: boolean }> =>
        fetch(
          `${BASE}/triagens/${triagemId}/candidatos/${respostaId}/arquivos/${encodeURIComponent(nome)}`,
          { method: "DELETE", headers: authHeaders() },
        ).then(handle),
    },
  },

  /**
   * DISC — análise de perfil comportamental. Mesmo caminho da Triagem: o RH
   * abre a aplicação do teste, manda o LINK pelo WhatsApp e o candidato
   * responde as 20 questões sem login. Quem corrige é o servidor, pelo gabarito
   * oficial do RH (server/disc-gabarito.ts, fora do bundle) — o candidato nunca
   * vê o resultado.
   * Ver server/disc.ts.
   */
  disc: {
    listar: (): Promise<DiscAplicacao[]> =>
      fetch(`${BASE}/disc/aplicacoes`, { headers: authHeaders() }).then(handle),

    /** A aplicação com os respondentes já corrigidos (campo `respostas`). */
    obter: (id: number): Promise<DiscAplicacao> =>
      fetch(`${BASE}/disc/aplicacoes/${id}`, { headers: authHeaders() }).then(handle),

    criar: (dados: Record<string, unknown>): Promise<{ id: number; token: string }> =>
      fetch(`${BASE}/disc/aplicacoes`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    atualizar: (id: number, dados: Record<string, unknown>): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/disc/aplicacoes/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    remover: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/disc/aplicacoes/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),

    /**
     * A IA ESCREVE as questões do cargo (o mesmo caminho da triagem).
     *
     * Só sugere: nada é gravado até o RH salvar o teste. Funciona antes de o
     * teste existir — nesse caso vai `0` no lugar do id, e a vaga viaja no corpo.
     */
    questoesIa: (
      id: number,
      vaga: Record<string, unknown>,
    ): Promise<{
      questoes: QuestaoDiscGabarito[];
      fonte: "ia" | "geral";
      avisos: string[];
      respondentes: number;
      mensagem: string;
    }> =>
      fetch(`${BASE}/disc/aplicacoes/${id}/questoes-ia`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(vaga),
      }).then(handle),

    /**
     * O RH lança o teste que foi respondido NO PAPEL (o formulário impresso
     * continua sendo usado nas entrevistas presenciais). A correção é a mesma.
     */
    lancarResposta: (
      aplicacaoId: number,
      dados: Record<string, unknown>,
    ): Promise<{ ok: boolean; id: number; mensagem: string }> =>
      fetch(`${BASE}/disc/aplicacoes/${aplicacaoId}/respostas`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    removerResposta: (aplicacaoId: number, respostaId: number): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/disc/aplicacoes/${aplicacaoId}/respostas/${respostaId}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /** Grava o parecer do RH sobre o candidato (o laudo sai com ele). */
    parecer: (
      respostaId: number,
      dados: { parecer?: string | null; observacao?: string | null },
    ): Promise<{ ok: boolean; mensagem: string }> =>
      fetch(`${BASE}/disc/respostas/${respostaId}/parecer`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /**
     * A IA SUGERE o parecer (nada é gravado até o RH salvar). `fonte` diz se o
     * texto veio da OpenAI ("ia") ou do parecer padrão do gabarito ("padrao") —
     * a tela mostra isso para o RH saber o que está lendo.
     */
    parecerIa: (respostaId: number): Promise<{ parecer: string; fonte: "ia" | "padrao" }> =>
      fetch(`${BASE}/disc/respostas/${respostaId}/parecer-ia`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: "{}",
      }).then(handle),
  },

  /**
   * DOSSIÊ DO CANDIDATO na aba Entrevistas: o CURRÍCULO que veio da Triagem
   * (mais os documentos entregues na entrevista) e o que a pessoa respondeu nas
   * perguntas estratégicas — tudo pelo id da ENTREVISTA, para dar para ler
   * durante a conversa. Ver server/candidato-entrevista.ts.
   */
  candidatoEntrevista: {
    obter: (entrevistaId: number): Promise<DossieCandidato> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/candidato`, { headers: authHeaders() }).then(handle),

    /** Candidatos da triagem parecidos com esta entrevista (para vincular). */
    sugestoes: (
      entrevistaId: number,
      q = "",
    ): Promise<{ candidato: string; sugestoes: SugestaoCandidato[] }> =>
      fetch(
        `${BASE}/entrevistas/${entrevistaId}/candidato/sugestoes${q ? `?q=${encodeURIComponent(q)}` : ""}`,
        { headers: authHeaders() },
      ).then(handle),

    vincular: (entrevistaId: number, respostaId: number): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/candidato`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ triagem_resposta_id: respostaId }),
      }).then(handle),

    desvincular: (entrevistaId: number): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/candidato`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /**
     * DISC da pessoa na entrevista. O resultado chega sozinho (o dossiê já traz
     * em `disc`); estas três são para quando o RH precisa escolher à mão.
     */
    discSugestoes: (
      entrevistaId: number,
      q = "",
    ): Promise<{ candidato: string; sugestoes: SugestaoDisc[] }> =>
      fetch(
        `${BASE}/entrevistas/${entrevistaId}/disc/sugestoes${q ? `?q=${encodeURIComponent(q)}` : ""}`,
        { headers: authHeaders() },
      ).then(handle),

    vincularDisc: (entrevistaId: number, discRespostaId: number): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/disc`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ disc_resposta_id: discRespostaId }),
      }).then(handle),

    desvincularDisc: (entrevistaId: number): Promise<{ mensagem: string }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/disc`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * CONVITE DE ENTREVISTA: o link que o candidato recebe depois de agendada a
   * entrevista — dia, horário, endereço da SERCAMP e quem procurar na
   * recepção. Ver server/convite-entrevista.ts.
   */
  conviteEntrevista: {
    /** O convite desta entrevista (ou null) + o local padrão já salvo. */
    get: (entrevistaId: number): Promise<ConviteEntrevistaResposta> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/convite`, { headers: authHeaders() }).then(handle),

    /**
     * Cria ou atualiza o convite. O token é PRESERVADO na atualização: o link
     * que o candidato já recebeu não pode morrer por causa de uma correção de
     * endereço (para trocar o link de propósito, mande `regerar: true`).
     */
    salvar: (
      entrevistaId: number,
      dados: Record<string, unknown>,
    ): Promise<{
      ok: boolean;
      convite: ConviteEntrevista;
      mensagem_modelo: string;
      mensagem: string;
    }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/convite`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** O RH confirma que entregou o link (carimba o dia/hora combinados). */
    enviado: (
      entrevistaId: number,
    ): Promise<{ ok: boolean; enviado_em: string; mensagem: string }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/convite/enviado`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    /** Derruba o link (o candidato deixa de conseguir abrir). Só administrador. */
    revogar: (entrevistaId: number): Promise<{ ok: boolean; mensagem: string }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/convite`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * RETORNO AO CANDIDATO — o resultado do processo seletivo (positivo ou
   * negativo) pelo WhatsApp. Ver server/retorno-entrevista.ts.
   */
  retornoEntrevista: {
    /** A entrevista, a sugestão dos campos, o histórico e os dois modelos. */
    get: (entrevistaId: number): Promise<RetornoEntrevistaResposta> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/retorno`, { headers: authHeaders() }).then(handle),

    /** Registra o retorno mandado (a tela chama ao abrir o WhatsApp). */
    registrar: (
      entrevistaId: number,
      dados: { tipo: TipoRetorno; telefone: string; mensagem: string },
    ): Promise<{ ok: boolean; envio: RetornoEnvio; mensagem: string }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/retorno`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    /** Ajusta o modelo do positivo ou do negativo (vale para os próximos). */
    salvarModelo: (
      tipo: TipoRetorno,
      texto: string,
    ): Promise<{ ok: boolean; modelos: Record<TipoRetorno, ModeloRetorno>; mensagem: string }> =>
      fetch(`${BASE}/entrevistas/retorno/modelos`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ tipo, texto }),
      }).then(handle),

    /** "Voltar ao texto original" — o modelo de fábrica volta a valer. */
    restaurarModelo: (
      tipo: TipoRetorno,
    ): Promise<{ ok: boolean; modelos: Record<TipoRetorno, ModeloRetorno>; mensagem: string }> =>
      fetch(`${BASE}/entrevistas/retorno/modelos?tipo=${encodeURIComponent(tipo)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * ROTEIRO DE ENTREVISTA: o guia que mapeia a entrevista inteira (blocos,
   * perguntas, notas e parecer). Ver server/roteiro-entrevista.ts.
   */
  roteiroEntrevista: {
    obter: (entrevistaId: number): Promise<{ entrevista: Row; roteiro: Row | null }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/roteiro`, { headers: authHeaders() }).then(handle),

    salvar: (
      entrevistaId: number,
      dados: Record<string, unknown>,
    ): Promise<{ ok: boolean; nota_final: number | null; status: string | null; mensagem: string }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/roteiro`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    remover: (entrevistaId: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/roteiro`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),

    /** A IA SUGERE perguntas técnicas do cargo (nada é gravado até salvar). */
    perguntasIa: (
      entrevistaId: number,
      dados: Record<string, unknown>,
    ): Promise<{ perguntas: { texto: string; dica?: string }[] }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/roteiro/perguntas-ia`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),
  },

  /**
   * PESQUISA DE CLIMA ORGANIZACIONAL: o questionário oficial da SERCAMP, o
   * link de cada colaborador e as respostas. Ver server/clima.ts.
   */
  clima: {
    list: (): Promise<ClimaPesquisaResumo[]> =>
      fetch(`${BASE}/clima/pesquisas`, { headers: authHeaders() }).then(handle),

    get: (id: number): Promise<ClimaPesquisa> =>
      fetch(`${BASE}/clima/pesquisas/${id}`, { headers: authHeaders() }).then(handle),

    create: (data: Record<string, unknown>): Promise<{ ok: boolean; id: number }> =>
      fetch(`${BASE}/clima/pesquisas`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    update: (id: number, data: Record<string, unknown>): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/clima/pesquisas/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(data),
      }).then(handle),

    remove: (id: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/clima/pesquisas/${id}`, { method: "DELETE", headers: authHeaders() }).then(handle),

    /** Gera o link de cada colaborador (não duplica quem já tem). */
    gerarConvites: (
      id: number,
      dados: { colaborador_ids?: number[]; departamento?: string },
    ): Promise<{ ok: boolean; criados: number; ja_tinham: number; total: number }> =>
      fetch(`${BASE}/clima/pesquisas/${id}/convites`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    marcarEnviado: (conviteId: number, desfazer = false): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/clima/convites/${conviteId}/enviado`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify({ desfazer }),
      }).then(handle),

    removerConvite: (conviteId: number): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/clima/convites/${conviteId}`, { method: "DELETE", headers: authHeaders() }).then(handle),
  },

  list: (resource: string): Promise<Row[]> =>
    fetch(`${BASE}/${resource}`, { headers: authHeaders() }).then(handle),

  /** Lista sem o filtro padrão do recurso (ex.: cargos arquivados junto). */
  listTodos: (resource: string): Promise<Row[]> =>
    fetch(`${BASE}/${resource}?todos=1`, { headers: authHeaders() }).then(handle),

  /** Plano de Cargos e Salários 2026 — ver server/plano-cargos.ts. */
  planoCargos: {
    acesso: (): Promise<{
      podeEditar: boolean;
      ehAdmin: boolean;
      responsaveis?: {
        usuario: string;
        nome: string | null;
        cargo: string | null;
        admin: boolean;
        responsavel: boolean;
      }[];
    }> => fetch(`${BASE}/cargos-plano/acesso`, { headers: authHeaders() }).then(handle),

    salvarResponsaveis: (usuarios: string[]): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/cargos-plano/responsaveis`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ usuarios }),
      }).then(handle),

    /** Movimentações lançadas + promoções da aba Promoções. */
    movimentacoes: (): Promise<Row[]> =>
      fetch(`${BASE}/cargos-plano/movimentacoes`, { headers: authHeaders() }).then(handle),
  },

  create: (resource: string, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  update: (resource: string, id: number, data: Row): Promise<Row> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
    }).then(handle),

  remove: (resource: string, id: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/${resource}/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }).then(handle),

  dashboard: (): Promise<DashboardData> =>
    fetch(`${BASE}/dashboard`, { headers: authHeaders() }).then(handle),

  /**
   * Retorna { usuario, cargo, isAdmin, podeExcluir } do usuário informado.
   * `podeExcluir` é o que esconde os botões de lixeira de quem não é admin.
   */
  me: (
    usuario: string,
  ): Promise<{
    usuario: string;
    nome: string | null;
    cargo: string | null;
    isAdmin: boolean;
    podeExcluir: boolean;
  }> => fetch(`${BASE}/me?u=${encodeURIComponent(usuario)}`).then(handle),

  /** Ficha 360° consolidada (RH+DP local + Operação/Qualidade/Almoxarifado por HTTP). */
  /** Formulário oficial de avaliação (FO 094): catálogo, aplicação e leitura. */
  avaliacaoFormulario: {
    catalogo: (): Promise<{
      codigo_formulario: string;
      niveis: NivelDesempenho[];
      instrucoes: string[];
      modelos: ModeloFormulario[];
    }> => fetch(`${BASE}/avaliacoes/formularios`, { headers: authHeaders() }).then(handle),

    aplicar: (dados: Record<string, unknown>): Promise<{ ok: boolean; id: number; nota: number | null }> =>
      fetch(`${BASE}/avaliacoes/aplicar`, {
        method: "POST",
        headers: { "content-type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    respostas: (id: number): Promise<{ avaliacao: Row; modelo: ModeloFormulario | null }> =>
      fetch(`${BASE}/avaliacoes/${id}/respostas`, { headers: authHeaders() }).then(handle),
  },

  /** Plano de ação / treinamentos do colaborador (aparece na avaliação). */
  treinamentosDoColaborador: (colaboradorId: number): Promise<Row[]> =>
    fetch(`${BASE}/colaboradores/${colaboradorId}/treinamentos`, { headers: authHeaders() }).then(handle),

  ficha: (colaboradorId: number): Promise<FichaData> =>
    fetch(`${BASE}/colaboradores/${colaboradorId}/ficha`, { headers: authHeaders() }).then(handle),

  /** IA do RH: sugestão de treinamentos / engajamento + conclusão de treinamento. */
  rhIa: {
    sugerirTreinamentos: (colaboradorId: number): Promise<SugestaoTreino> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/sugerir-treinamentos`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    aplicarTreinamentos: (colaboradorId: number, treinamentos: Row[]): Promise<{ ok: boolean; criados: number }> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/sugerir-treinamentos/aplicar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ treinamentos }),
      }).then(handle),

    concluirTreinamento: (treinamentoId: number, form: FormData): Promise<{ ok: boolean; certificado: string | null; skill_atualizada: string }> =>
      fetch(`${BASE}/treinamentos/${treinamentoId}/concluir`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
      }).then(handle),

    sugerirEngajamento: (engajamentoId: number): Promise<SugestaoEngaj> =>
      fetch(`${BASE}/engajamento/${engajamentoId}/sugerir-acoes`, {
        method: "POST",
        headers: authHeaders(),
      }).then(handle),

    salvarEngajamento: (engajamentoId: number, texto: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/engajamento/${engajamentoId}/salvar-acoes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ texto }),
      }).then(handle),
  },

  /** Pasta de arquivos/certificados do colaborador. */
  arquivos: {
    list: (colaboradorId: number): Promise<ArquivoInfo[]> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos`, { headers: authHeaders() }).then(handle),

    upload: (colaboradorId: number, files: File[] | FileList): Promise<{ ok: boolean; salvos: string[] }> => {
      const fd = new FormData();
      for (const f of Array.from(files)) fd.append("file", f);
      return fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    url: (colaboradorId: number, nome: string): string =>
      `${BASE}/colaboradores/${colaboradorId}/arquivos/${encodeURIComponent(nome)}`,

    remove: (colaboradorId: number, nome: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/colaboradores/${colaboradorId}/arquivos/${encodeURIComponent(nome)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /**
   * Documentos do CANDIDATO na aba Entrevistas (currículo, RG, CPF, comprovantes
   * entregues na entrevista). Mesma forma de `arquivos`, mas guardados por
   * ENTREVISTA — candidato ainda não é colaborador e pode nem ser contratado.
   */
  documentosEntrevista: {
    list: (entrevistaId: number): Promise<ArquivoInfo[]> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/arquivos`, { headers: authHeaders() }).then(handle),

    upload: (entrevistaId: number, files: File[] | FileList): Promise<{ ok: boolean; salvos: string[] }> => {
      const fd = new FormData();
      for (const f of Array.from(files)) fd.append("file", f);
      return fetch(`${BASE}/entrevistas/${entrevistaId}/arquivos`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },

    url: (entrevistaId: number, nome: string): string =>
      `${BASE}/entrevistas/${entrevistaId}/arquivos/${encodeURIComponent(nome)}`,

    remove: (entrevistaId: number, nome: string): Promise<{ ok: boolean }> =>
      fetch(`${BASE}/entrevistas/${entrevistaId}/arquivos/${encodeURIComponent(nome)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }).then(handle),
  },

  /** Certificado do colaborador anexado a um treinamento (vai para a pasta dele). */
  treinamentos: {
    uploadCertificado: (
      treinamentoId: number,
      files: File[] | FileList,
    ): Promise<{ ok: boolean; salvos: string[]; colaborador_id: number }> => {
      const fd = new FormData();
      for (const f of Array.from(files)) fd.append("file", f);
      return fetch(`${BASE}/treinamentos/${treinamentoId}/certificado`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      }).then(handle);
    },
  },

  /** Links de avaliação por setor (o RH gera; o gestor preenche na página pública). */
  avaliacaoLinks: {
    criar: (dados: {
      /** Obrigatório no link do setor; no link individual sai do cadastro. */
      departamento?: string;
      gestor_nome: string;
      gestor_email?: string;
      periodo?: string;
      /** Link de UM colaborador (sem isto, é o link do setor inteiro). */
      colaborador_id?: number;
      /**
       * Quem o RH marcou na lista do setor. Sem isto (ou vazio), o link vale
       * para o setor inteiro — é como os links antigos funcionam.
       */
      colaborador_ids?: number[];
    }): Promise<AvaliacaoLink> =>
      fetch(`${BASE}/avaliacoes/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(dados),
      }).then(handle),

    listar: (): Promise<AvaliacaoLink[]> =>
      fetch(`${BASE}/avaliacoes/links`, { headers: authHeaders() }).then(handle),

    /**
     * Quem o setor traria para o link — a lista que a tela mostra com caixinhas
     * para o RH desmarcar quem não deve entrar na avaliação daquele gestor.
     */
    elegiveis: (
      departamento: string,
    ): Promise<{ departamento: string; colaboradores: ElegivelAvaliacao[] }> =>
      fetch(`${BASE}/avaliacoes/links/elegiveis?departamento=${encodeURIComponent(departamento)}`, {
        headers: authHeaders(),
      }).then(handle),

    /**
     * O que o gestor SALVOU e ainda não enviou naquele link. "Salvar" grava
     * rascunho; só "Concluir e enviar" vira avaliação — e o preenchimento
     * ficava invisível para o RH.
     */
    rascunhos: (
      linkId: number,
    ): Promise<{
      link: { id: number; gestor_nome: string; departamento: string | null; periodo: string | null; status: string };
      rascunhos: RascunhoAvaliacao[];
    }> => fetch(`${BASE}/avaliacoes/links/${linkId}/rascunhos`, { headers: authHeaders() }).then(handle),

    /** Transforma o que está salvo em avaliação, sem depender do gestor voltar. */
    efetivar: (linkId: number, periodo?: string): Promise<{ ok: boolean; criadas: number }> =>
      fetch(`${BASE}/avaliacoes/links/${linkId}/efetivar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ periodo }),
      }).then(handle),

    consolidado: (): Promise<ConsolidadoAvaliacoes> =>
      fetch(`${BASE}/avaliacoes/consolidado`, { headers: authHeaders() }).then(handle),

    /**
     * TODAS as avaliações já realizadas de um colaborador, com as respostas
     * questão a questão e o formulário usado em cada uma. É o que o "olhinho"
     * da lista abre — ver components/AvaliacoesColaboradorModal.tsx.
     */
    doColaborador: (
      colaboradorId: number,
    ): Promise<{ colaborador: Row | null; avaliacoes: AvaliacaoRealizada[] }> =>
      fetch(`${BASE}/avaliacoes/colaborador/${colaboradorId}`, { headers: authHeaders() }).then(handle),

    /** Uma avaliação específica, com o formulário para montar a leitura. */
    respostas: (avaliacaoId: number): Promise<{ avaliacao: AvaliacaoRealizada; modelo: ModeloFormulario | null }> =>
      fetch(`${BASE}/avaliacoes/${avaliacaoId}/respostas`, { headers: authHeaders() }).then(handle),
  },
};

/**
 * Página PÚBLICA da avaliação (o gestor não faz login — o token é a credencial).
 * Não envia o header de sessão.
 */
export const avaliacaoPublica = {
  obter: (token: string): Promise<FormularioAvaliacao> =>
    fetch(`${BASE}/avaliacoes/formulario/${encodeURIComponent(token)}`).then(handle),

  enviar: (
    token: string,
    payload: {
      periodo?: string;
      /** Uma por colaborador, com as notas das questões do formulário dele. */
      avaliacoes: {
        colaborador_id: number;
        modelo: string | null;
        respostas: Record<string, string>;
        pontos_fortes: string;
        pontos_melhoria: string;
        comentario: string;
      }[];
    },
  ): Promise<{ ok: boolean; criadas: number }> =>
    fetch(`${BASE}/avaliacoes/formulario/${encodeURIComponent(token)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),

  /**
   * Salva o preenchimento de UM colaborador sem encerrar o formulário — o
   * botão "Salvar" de cada pessoa. O link continua pendente e o gestor volta
   * pelo mesmo endereço para continuar de onde parou.
   */
  /**
   * O que o gestor JÁ ENVIOU por este link (somente leitura). O link é de uso
   * único: sem isto, reabrir o endereço só dizia "já foi respondido" e ele não
   * tinha como conferir o que preencheu.
   */
  enviadas: (
    token: string,
  ): Promise<{ link: { gestor_nome: string | null; departamento: string | null; respondido_em: string | null }; avaliacoes: AvaliacaoRealizada[] }> =>
    fetch(`${BASE}/avaliacoes/formulario/${encodeURIComponent(token)}/enviadas`).then(handle),

  salvarRascunho: (
    token: string,
    dados: {
      colaborador_id: number;
      modelo: string | null;
      respostas: Record<string, string>;
      pontos_fortes: string;
      pontos_melhoria: string;
      comentario: string;
      periodo?: string;
    },
  ): Promise<{ ok: boolean; colaborador_id: number; salvos: number; aviso: string | null }> =>
    fetch(`${BASE}/avaliacoes/formulario/${encodeURIComponent(token)}/rascunho`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dados),
    }).then(handle),
};

/**
 * Página PÚBLICA da pesquisa de clima (o colaborador não faz login — o token
 * do link é a credencial). Não envia o header de sessão.
 */
export const climaPublica = {
  obter: (token: string): Promise<ClimaFormulario> =>
    fetch(`${BASE}/clima/formulario/${encodeURIComponent(token)}`).then(handle),

  enviar: (
    token: string,
    payload: {
      setor?: string;
      tempo_empresa?: string;
      respostas: { id: string; nota?: number | null; resposta?: string }[];
    },
  ): Promise<{ ok: boolean; media: number | null; mensagem: string }> =>
    fetch(`${BASE}/clima/formulario/${encodeURIComponent(token)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then(handle),
};

/* ----------------------- Pesquisa de clima: tipos ----------------------- */

export interface ClimaPergunta {
  id: string;
  texto: string;
  tipo: "escala" | "aberta";
  dimensao: string;
  enps?: boolean;
}

export interface ClimaPesquisaResumo {
  id: number;
  titulo: string;
  periodo: string | null;
  identificada: boolean;
  status: string; // Aberta | Encerrada
  criado_por: string | null;
  criado_em: string;
  expira_em: string | null;
  perguntas: ClimaPergunta[];
  convites: number;
  respondidos: number;
  respostas: number;
  media: number | null;
}

export interface ClimaConvite {
  id: number;
  colaborador_id: number | null;
  nome: string | null;
  email: string | null;
  telefone: string | null;
  setor: string | null;
  cargo: string | null;
  token: string;
  status: string; // Pendente | Respondido
  enviado_em: string | null;
  respondido_em: string | null;
}

/** Uma pergunta já respondida (o que o colaborador marcou). */
export interface ClimaRespostaItem {
  id: string;
  texto: string;
  tipo: "escala" | "aberta";
  dimensao: string;
  nota: number | null;
  rotulo: string | null;
  resposta: string | null;
}

export interface ClimaResposta {
  id: number;
  colaborador_id: number | null;
  colaborador_nome: string | null;
  setor: string | null;
  tempo_empresa: string | null;
  respostas: ClimaRespostaItem[];
  media: number | null;
  pontuacao: number | null;
  enps: number | null;
  comentario: string | null;
  respondido_em: string;
}

export interface ClimaResultados {
  geral: {
    media: number | null;
    pontuacao: number | null;
    respostas: number;
    convites: number;
    pendentes: number;
    participacao: number | null;
    enps: number | null;
    promotores: number;
    neutros: number;
    detratores: number;
  };
  porPergunta: {
    id: string;
    texto: string;
    dimensao: string;
    media: number | null;
    pontuacao: number | null;
    respostas: number;
    distribuicao: Record<string, number>;
  }[];
  porDimensao: { nome: string; media: number | null }[];
  porSetor: { nome: string; media: number | null; respostas: number }[];
  porTempoEmpresa: { nome: string; media: number | null; respostas: number }[];
  comentarios: { nome: string | null; setor: string | null; texto: string; respondido_em: string }[];
}

/** A pesquisa aberta na tela: com os links de cada um e as respostas. */
export interface ClimaPesquisa {
  id: number;
  titulo: string;
  periodo: string | null;
  apresentacao: string | null;
  perguntas: ClimaPergunta[];
  identificada: boolean;
  status: string;
  criado_por: string | null;
  criado_em: string;
  expira_em: string | null;
  convites: ClimaConvite[];
  respostas: ClimaResposta[];
  resultados: ClimaResultados;
  escala: { valor: number; rotulo: string }[];
  tempos: string[];
}

export interface ClimaFormulario {
  titulo: string;
  empresa: string;
  periodo: string | null;
  apresentacao: string;
  confidencialidade: string;
  agradecimento: string;
  identificada: boolean;
  colaborador: string | null;
  setor: string;
  tempo_empresa: string;
  escala: { valor: number; rotulo: string }[];
  tempos: string[];
  perguntas: ClimaPergunta[];
  respondido: boolean;
  encerrada: boolean;
  expirado: boolean;
}

export interface SugestaoTreino {
  colaborador: { id: number; nome: string; cargo: string | null };
  diagnostico: string;
  fonte: "ia" | "padrao";
  treinamentos: { nome_treinamento: string; carga_horaria: number; prioridade: string; justificativa: string }[];
}

export interface SugestaoEngaj {
  engajamento: { id: number; colaborador: string };
  diagnostico: string;
  fonte: "ia" | "padrao";
  acoes: { acao: string; categoria: string; prazo_dias: number }[];
}

export interface ArquivoInfo {
  nome: string;
  tamanho: number;
  enviado_em: string;
  ext: string;
  /**
   * Endereço ASSINADO e temporário para abrir/baixar o arquivo.
   *
   * Vem pronto da listagem (que exige sessão) porque a rota de download deixou
   * de ser aberta — e `<a href>`/`<iframe>` não conseguem mandar o cabeçalho de
   * login. Ver server/arquivo-link.ts. Só falta em resposta de servidor antigo.
   */
  url?: string;
}

/* ------------------------------------------------------------------ *
 *  DISC (análise de perfil comportamental)                            *
 * ------------------------------------------------------------------ */

/** Uma aplicação do teste: a "vaga" que recebe os candidatos pelo link. */
export interface DiscAplicacao {
  id: number;
  nome: string;
  setor: string | null;
  cargo: string | null;
  descricao: string | null;
  /** Credencial da página pública (/disc/<token>). */
  token: string;
  /** "Aberta" | "Encerrada". */
  status: string;
  criado_por: string | null;
  criado_em: string;
  expira_em: string | null;
  /** Contagens da listagem. */
  respondentes?: number;
  sem_parecer?: number;
  /**
   * AS QUESTÕES DESTA VAGA (o DISC por cargo), com o fator de cada alternativa.
   *
   * Só vem no `obter`, que é rota com login: o fator é o gabarito, e o RH
   * precisa dele para editar o texto sem estragar a correção. A página do
   * candidato recebe outra lista, montada sem o fator (ver server/disc.ts).
   * Lista vazia (ou ausente) = a aplicação usa o questionário geral do papel.
   */
  questoes?: QuestaoDiscGabarito[];
  /** 'Geral' | 'IA' | 'Manual' — de onde vieram as questões. */
  questoes_origem?: OrigemQuestoes | null;
  questoes_por?: string | null;
  questoes_em?: string | null;
  /** Quantas questões próprias o teste tem (0 = usa as 20 do papel). */
  total_questoes?: number;
  /** O que conferir no questionário antes de mandar o link. */
  avisos_questoes?: string[];
  /** Só no `obter`: os candidatos com o teste já corrigido. */
  respostas?: DiscResposta[];
}

/** O teste de um candidato, já corrigido pelo servidor. */
export interface DiscResposta {
  id: number;
  candidato: string;
  email: string | null;
  telefone: string | null;
  /** Campo que o papel do RH pede ("Vaga pretendida"). */
  vaga_pretendida: string | null;
  /** O que ele marcou, questão a questão (para o RH conferir com o impresso). */
  respostas: { q: number; alternativa: string }[];
  /** Placar dos quatro fatores (0–20 cada). */
  d: number;
  i: number;
  s: number;
  c: number;
  /** "C/S" e "Conformidade com Estabilidade". */
  perfil: string | null;
  perfil_nome: string | null;
  /** 'Link' = respondeu sozinho; 'RH' = teste de papel digitado pelo RH. */
  origem: string;
  criado_por: string | null;
  parecer: string | null;
  parecer_por: string | null;
  parecer_em: string | null;
  observacao: string | null;
  respondido_em: string;
}

/* ------------------------------------------------------------------ *
 *  Dossiê do candidato na entrevista (Triagem ⇄ Entrevistas)          *
 * ------------------------------------------------------------------ */

/** Arquivo do dossiê: sabe de onde veio e já traz a URL para abrir. */
export interface ArquivoDossie extends ArquivoInfo {
  origem: "triagem" | "entrevista";
  /** No dossiê a URL assinada sempre vem (o servidor a monta). */
  url: string;
}

/** A vaga da triagem de onde o candidato veio. */
export interface VinculoTriagem {
  resposta_id: number;
  triagem_id: number;
  vaga: string;
  vaga_setor: string | null;
  vaga_cargo: string | null;
  vaga_descricao: string | null;
  vaga_status: string | null;
}

/** O que a pessoa respondeu na triagem e a avaliação que o RH deu. */
export interface CandidatoTriagem {
  nome: string;
  email: string | null;
  telefone: string | null;
  nota: number | string | null;
  situacao: string | null;
  observacao: string | null;
  origem: string | null;
  criado_por: string | null;
  avaliado_por: string | null;
  avaliado_em: string | null;
  respondido_em: string | null;
  respostas: { id: string; texto: string; resposta: string }[];
}

/**
 * O DISC da pessoa, ligado à entrevista (ver server/disc-entrevista.ts).
 *
 * `vinculo`: 'auto' = o sistema reconheceu sozinho (`como` diz por quê);
 * 'manual' = o RH escolheu no painel.
 */
export interface DiscDaEntrevista {
  vinculo: "auto" | "manual";
  como: "telefone" | "e-mail" | "nome" | null;
  resposta: DiscResposta;
  aplicacao: {
    id: number;
    nome: string;
    setor: string | null;
    cargo: string | null;
    questoes_origem: OrigemQuestoes | null;
  };
  /** As questões do teste da vaga, SEM o fator. Vazia = questionário geral do papel. */
  questoes: { n: number; enunciado: string; alternativas: { letra: string; texto: string }[] }[];
}

/** Teste DISC oferecido para ligar à mão a uma entrevista. */
export interface SugestaoDisc {
  resposta_id: number;
  candidato: string;
  telefone: string | null;
  email: string | null;
  perfil: string | null;
  respondido_em: string;
  /** Nome do teste (a aplicação do DISC). */
  aplicacao: string;
  aplicacao_cargo: string | null;
  /** A regra reconhece a pessoa por aqui (null = só sugestão). */
  como: "telefone" | "e-mail" | "nome" | null;
  /** O nome bate total ou parcialmente. */
  parecido: boolean;
  /** O RH já disse que não é esta pessoa. */
  recusado: boolean;
}

export interface DossieCandidato {
  entrevista: Row;
  /** null = entrevista lançada à mão, sem candidato da triagem ligado. */
  vinculo: VinculoTriagem | null;
  candidato: CandidatoTriagem | null;
  /** O DISC da pessoa (null = ainda não respondeu, ou não foi reconhecido). */
  disc: DiscDaEntrevista | null;
  arquivos: ArquivoDossie[];
  /** Arquivo que a tela abre primeiro, como "origem:nome" (o currículo). */
  principal: string | null;
}

/* ------------------------------------------------------------------ *
 *  Convite de entrevista (o link que o candidato recebe)              *
 * ------------------------------------------------------------------ */

/**
 * O convite gerado para UMA entrevista.
 *
 * Repare no que NÃO está aqui: dia, horário, etapa e entrevistador. Eles não
 * são copiados para o convite — a página do candidato lê tudo isso ao vivo da
 * entrevista, para que reagendar atualize sozinho o link que já está na mão da
 * pessoa. O convite guarda só a LOGÍSTICA (onde é, quem procurar, o que levar)
 * e o acompanhamento do envio. Ver server/convite-entrevista.ts.
 */
export interface ConviteEntrevista {
  id: number;
  token: string;
  /** Caminho da página pública ("/entrevista/<token>") — a tela monta a URL. */
  caminho: string;
  local_nome: string | null;
  endereco: string | null;
  complemento: string | null;
  referencia: string | null;
  mapa_url: string | null;
  /** Quem o candidato deve procurar ao chegar na recepção. */
  procurar_por: string | null;
  contato_nome: string | null;
  contato_telefone: string | null;
  orientacoes: string | null;
  documentos: string | null;
  /** Dia/hora que valiam quando o RH marcou o convite como enviado. */
  data_enviada: string | null;
  horario_enviado: string | null;
  enviado_em: string | null;
  aberto_em: string | null;
  aberturas: number;
  resposta: string | null;
  resposta_mensagem: string | null;
  respondido_em: string | null;
  criado_por: string | null;
  criado_em: string;
  atualizado_em: string | null;
  /** A entrevista mudou de dia/hora DEPOIS do envio — avisar o candidato. */
  mudou_depois_do_envio: boolean;
}

/** O local padrão que o RH salvou — preenche o formulário do próximo convite. */
export interface ConviteEntrevistaPadrao {
  local_nome: string | null;
  endereco: string | null;
  complemento: string | null;
  referencia: string | null;
  mapa_url: string | null;
  procurar_por: string | null;
  contato_nome: string | null;
  contato_telefone: string | null;
  orientacoes: string | null;
  documentos: string | null;
}

export interface ConviteEntrevistaResposta {
  entrevista: Row;
  /** Dados de contato do candidato — só existem se a entrevista veio da Triagem. */
  candidato: { nome: string; email: string | null; telefone: string | null } | null;
  vaga: string | null;
  convite: ConviteEntrevista | null;
  padrao: ConviteEntrevistaPadrao;
  /** Texto pronto para WhatsApp/e-mail, com o marcador {link}. */
  mensagem_modelo: string;
}

/** Um retorno já mandado ao candidato (o histórico da entrevista). */
export interface RetornoEnvio {
  id: number;
  tipo: TipoRetorno;
  /** Só dígitos, com o 55 ("5519999998888"); null = mandado sem número. */
  telefone: string | null;
  mensagem: string;
  enviado_por: string | null;
  enviado_em: string;
}

/** Um dos dois modelos (o de fábrica ou o ajustado pelo RH). */
export interface ModeloRetorno {
  texto: string;
  /** false = é o texto de fábrica. */
  personalizado: boolean;
  atualizado_por: string | null;
  atualizado_em: string | null;
}

export interface RetornoEntrevistaResposta {
  entrevista: {
    id: number;
    candidato: string;
    cargo: string | null;
    data: string | null;
    horario: string | null;
    status: string | null;
  };
  /** Dados de contato do candidato — só existem se a entrevista veio da Triagem. */
  candidato: { nome: string; email: string | null; telefone: string | null } | null;
  vaga: string | null;
  /** O que os campos já trazem preenchido. */
  sugestao: {
    nome: string;
    vaga: string;
    telefone: string;
    /** De onde veio o telefone: último retorno, triagem ou DISC. */
    telefone_origem: "retorno" | "triagem" | "disc" | null;
  };
  envios: RetornoEnvio[];
  modelos: Record<TipoRetorno, ModeloRetorno>;
}

/* ------------------------------------------------------------------ *
 *  Programas internos (Realize um Sonho / Ideias que Transformam)     *
 * ------------------------------------------------------------------ */

/** Uma edição do Sonho ou uma rodada de Ideias — é ela que tem o link. */
export interface CampanhaPrograma {
  id: number;
  programa: "sonho" | "ideia";
  titulo: string;
  descricao: string | null;
  token: string;
  status: string; // Aberta | Encerrada
  criado_por: string | null;
  criado_em: string;
  expira_em: string | null;
  respostas: number;
  /** Quantas ainda estão em "Recebida" (o que o RH ainda não olhou). */
  novas: number;
}

/**
 * Uma resposta. Os campos variam entre os dois programas (o do Sonho tem
 * `sonho`/`motivo`/`valor`; o da Ideia tem `categoria`/`titulo`/`descricao`),
 * por isso o índice livre — quem lê sabe de qual programa veio.
 */
export interface RespostaPrograma {
  id: number;
  campanha_id: number | null;
  campanha_titulo: string | null;
  nome: string;
  telefone: string | null;
  setor: string | null;
  funcao: string | null;
  situacao: string;
  observacao: string | null;
  avaliado_por: string | null;
  avaliado_em: string | null;
  origem: string;
  respondido_em: string;
  /** Casado pelo NOME com o cadastro — null quando não é (ainda) colaborador. */
  colaborador_id: number | null;
  colaborador_nome: string | null;
  colaborador_departamento: string | null;
  colaborador_cargo: string | null;
  [campo: string]: unknown;
}

/** Candidato da triagem oferecido para vincular a uma entrevista solta. */
export interface SugestaoCandidato {
  resposta_id: number;
  triagem_id: number;
  candidato: string;
  email: string | null;
  telefone: string | null;
  nota: number | string | null;
  situacao: string | null;
  respondido_em: string | null;
  vaga: string;
  vaga_setor: string | null;
  /** Já está ligado a OUTRA entrevista — a tela avisa antes de deixar trocar. */
  ja_vinculado: boolean;
  /** O nome bate (total ou parcialmente) com o da entrevista. */
  parecido: boolean;
}

/** Um candidato a entrar no link do setor (com o formulário FO 094 dele). */
export interface ElegivelAvaliacao {
  id: number;
  nome: string;
  cargo: string | null;
  departamento?: string | null;
  empresa_nome: string;
  /** null = o cargo não casou com nenhum formulário — costuma ser quem está no setor errado. */
  modelo: string | null;
  modelo_nome: string | null;
}

export interface AvaliacaoLink {
  id: number;
  token: string;
  departamento: string;
  gestor_nome: string;
  gestor_email: string | null;
  periodo: string | null;
  status: string; // Pendente | Respondido
  criado_em?: string;
  respondido_em?: string | null;
  expira_em?: string | null;
  expirado?: boolean;
  colaboradores?: number; // qtd de colaboradores ativos no setor
  /** Quantos já foram preenchidos e salvos pelo gestor — sem enviar ainda. */
  rascunhos?: number;
  rascunho_em?: string | null;
}

export interface CriterioAvaliacao {
  chave: string;
  label: string;
}

/** Uma questão do formulário oficial (FO 094). */
export interface QuestaoFormulario {
  chave: string;
  titulo: string;
  descricao: string;
  tipo: "geral" | "especifica";
}

export interface ModeloFormulario {
  key: string;
  nome: string;
  departamento: string;
  /** Empresa dona do formulário: "sercamp" | "moveme". */
  empresa?: string;
  questoes: QuestaoFormulario[];
}

/**
 * Uma avaliação JÁ REALIZADA, como sai do servidor para a leitura: além do que
 * está gravado, vem o `modelo` (o formulário FO 094 usado), para a tela casar
 * cada nota com o texto da questão. `criterios` é { chave da questão: nota }.
 */
export interface AvaliacaoRealizada {
  id: number;
  colaborador_id: number | null;
  colaborador_nome?: string | null;
  cargo?: string | null;
  periodo: string | null;
  nota: number | null;
  avaliador: string | null;
  comentario: string | null;
  origem: string | null;
  setor: string | null;
  criterios: Record<string, number> | null;
  link_token?: string | null;
  modelo_nome: string | null;
  pontos_fortes: string | null;
  pontos_melhoria: string | null;
  created_at: string | null;
  /** O formulário usado (null quando o cargo não casa com nenhum). */
  modelo: ModeloFormulario | null;
}

/** Preenchimento salvo pelo gestor e ainda NÃO enviado (avaliacao_rascunhos). */
export interface RascunhoAvaliacao {
  colaborador_id: number;
  colaborador_nome: string | null;
  cargo: string | null;
  atualizado_em: string | null;
  /** A nota que viraria avaliação se o RH efetivasse agora. */
  nota: number | null;
  criterios: Record<string, number> | null;
  modelo: ModeloFormulario | null;
  modelo_nome: string | null;
  pontos_fortes: string | null;
  pontos_melhoria: string | null;
  comentario: string | null;
  /** false = salvou sem nenhuma nota; efetivar não geraria avaliação. */
  aproveitavel: boolean;
}

export interface NivelDesempenho {
  nivel: string;
  faixa: string;
  de: number;
  ate: number;
  descricao: string;
}

export interface FormularioAvaliacao {
  departamento: string;
  gestor_nome: string;
  periodo: string | null;
  status: string;
  respondido: boolean;
  expirado: boolean;
  codigo_formulario: string;
  /** Empresa do link, quando todo mundo nele é da mesma ("sercamp"/"moveme"). */
  empresa: string | null;
  empresa_nome: string | null;
  niveis: NivelDesempenho[];
  instrucoes: string[];
  empresas: { key: string; nome: string }[];
  modelos: ModeloFormulario[];
  /** Cada colaborador já vem com o formulário do cargo dele, na empresa dele. */
  colaboradores: {
    id: number;
    nome: string;
    cargo: string | null;
    empresa: string;
    empresa_nome: string;
    modelo: string | null;
    modelo_nome: string | null;
    questoes: QuestaoFormulario[];
  }[];
  /**
   * O que já foi SALVO sem enviar (um por colaborador). É com isto que a página
   * reabre preenchida quando o gestor volta para terminar.
   */
  rascunhos?: {
    colaborador_id: number;
    modelo: string | null;
    respostas: Record<string, string>;
    pontos_fortes: string;
    pontos_melhoria: string;
    observacoes: string;
    atualizado_em: string;
  }[];
}

export interface ConsolidadoAvaliacoes {
  geral: { media: number | null; total: number };
  porColaborador: {
    colaborador_id: number;
    nome: string;
    setor: string | null;
    media: number;
    qtd: number;
    ultimo_periodo: string | null;
  }[];
  porSetor: { setor: string; media: number; qtd: number }[];
  links: { pendentes: number; respondidos: number };
}

/** Ficha 360° — formato genérico (cards + seções por bloco/departamento). */
export interface FichaCard {
  label: string;
  valor: string | number;
  tom?: string;
}
export interface FichaSecao {
  titulo: string;
  colunas: string[];
  linhas: (string | number | null)[][];
  vazioMsg?: string;
}
export interface FichaBloco {
  sistema: string;
  titulo: string;
  status: "ok" | "vazio" | "indisponivel";
  casadoPor: string | null;
  cards: FichaCard[];
  secoes: FichaSecao[];
}
export interface FichaData {
  /**
   * O cadastro INTEIRO da pessoa (a ficha mostra todos os campos, inclusive os
   * que vieram da planilha do RH: RG, PIS, CTPS, filiação, endereço, banco…).
   */
  colaborador: {
    id: number;
    nome: string;
    cpf: string | null;
    email: string | null;
    cargo: string | null;
    departamento: string | null;
    status: string | null;
    data_admissao: string | null;
    skills: string | null;
    [campo: string]: unknown;
  };
  blocos: FichaBloco[];
  _meta: { indisponiveis: string[]; casadosPorNome: string[] };
}

/* ---------------- Endomarketing: mídias do aviso e fundos do cartão ---------------- */

/** O que pode ir junto da mensagem do comunicado. */
export type TipoMidia = "imagem" | "video" | "pdf";

/** Uma mídia do comunicado, como o histórico devolve (o tipo vem do servidor). */
export interface MidiaComunicado {
  /** Nome do arquivo no servidor (pasta das artes). */
  arquivo: string;
  /** Nome original — o que aparece na tela e no arquivo baixado. */
  nome: string;
  tipo: TipoMidia;
  tamanho: number | null;
}

/** Fundo salvo para o cartão de aniversário. */
export interface FundoCartao {
  id: number;
  arquivo: string;
  nome: string | null;
  criado_por: string | null;
  criado_em: string;
  /** false = o arquivo sumiu do disco (a tela não mostra). */
  disponivel: boolean;
  url: string;
}
